import React, { useState, useEffect, useContext, useCallback } from 'react';
import axiosInstance from '../axiosConfig';
import { buildApiUrl, buildMediaUrl } from '../apiConfig';
import { AuthContext } from '../AuthContext';
import { Spinner, Button, Table, Form, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';

function PurchaseOrderDetails({ orderId, onClose }) {
    const { authTokens } = useContext(AuthContext);
    const [orderItems, setOrderItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [orderName, setOrderName] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Для модального окна подтверждения удаления
    const [modalImage, setModalImage] = useState('');

    // Мемозируем функцию fetchOrderItems с помощью useCallback
    const fetchOrderItems = useCallback(() => {
        setLoading(true);
        axiosInstance.get(`/api/purchase-order/${orderId}/details/`, {
            headers: {
                'Authorization': `Bearer ${authTokens.access}`
            }
        })
        .then(response => {
            const items = response.data.items;
            setOrderItems(items);
            setOrderName(response.data.order_name);
        })
        .catch(error => {
            console.error('Ошибка при загрузке элементов заказа:', error);
            window.Toastify({
                text: "Ошибка при загрузке элементов заказа.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
        })
        .finally(() => setLoading(false));
    }, [orderId, authTokens.access]); // Указываем зависимости

    useEffect(() => {
        fetchOrderItems();
    }, [fetchOrderItems]); // Добавляем fetchOrderItems в зависимости

    const handleDeleteOrder = () => {
        axiosInstance.delete(`/api/purchase-order/${orderId}/delete/`, {
            headers: {
                'Authorization': `Bearer ${authTokens.access}`
            }
        })
        .then(() => {
            window.Toastify({
                text: "Заказ успешно удален!",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#28a745"
            }).showToast();
            onClose();
        })
        .catch(error => {
            console.error('Ошибка при удалении заказа:', error);
            window.Toastify({
                text: "Ошибка при удалении заказа.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
        })
        .finally(() => {
            setShowDeleteModal(false); // Закрываем модальное окно после удаления
        });
    };

    const handleQuantityChange = (itemId, quantity) => {
        setOrderItems(prevItems => 
            prevItems.map(item => 
                item.id === itemId ? { ...item, quantity: quantity } : item
            )
        );
    };

    const handleSaveChanges = () => {
        const updatePromises = orderItems.map(item =>
            axiosInstance.post(`/api/purchase-order-item/${item.id}/update/`, {
                quantity: item.quantity
            }, {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            })
        );

        Promise.all(updatePromises)
            .then(() => {
                window.Toastify({
                    text: "Изменения успешно сохранены!",
                    duration: 3000,
                    gravity: "top",
                    position: "right",
                    backgroundColor: "#28a745"
                }).showToast();
            })
            .catch(error => {
                console.error('Ошибка при сохранении изменений:', error);
                window.Toastify({
                    text: "Ошибка при сохранении изменений.",
                    duration: 3000,
                    gravity: "top",
                    position: "right",
                    backgroundColor: "#ff0000"
                }).showToast();
            });
    };

    const handleRemoveItem = (itemId) => {
        axiosInstance.delete(`/api/purchase-order-item/${itemId}/delete/`, {
            headers: {
                'Authorization': `Bearer ${authTokens.access}`
            }
        })
        .then(() => {
            setOrderItems(prevItems => prevItems.filter(item => item.id !== itemId));
            window.Toastify({
                text: "Элемент успешно удален!",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#28a745"
            }).showToast();
        })
        .catch(error => {
            console.error('Ошибка при удалении элемента:', error);
            window.Toastify({
                text: "Ошибка при удалении элемента.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
        });
    };

    const handleExportExcel = () => {
        const url = buildApiUrl(`/api/purchase-order/${orderId}/export/`);
    
        axiosInstance.get(url, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${authTokens.access}`
            }
        })
        .then(response => {
            const fileName = `${orderName} #${orderId}.xlsx`;
            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        })
        .catch(error => {
            console.error('Ошибка при экспорте файла:', error);
            window.Toastify({
                text: "Ошибка при экспорте файла.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
        });
    };
    
    const openImageModal = (imageUrl) => {
        setModalImage(imageUrl);
        setShowModal(true);
    };

    return (
        <div className="purchase-order-details container">
            <h3>Детали заказа на закупку: {orderName} (ID: {orderId})</h3>
            {loading ? (
                <Spinner animation="border" />
            ) : (
                <>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table striped bordered hover className="table-fixed">
                            <thead>
                                <tr>
                                    <th style={{ position: 'sticky', top: 0 }}>Название</th>
                                    <th style={{ position: 'sticky', top: 0 }}>Описание</th>
                                    <th style={{ position: 'sticky', top: 0 }}>Фото</th>
                                    <th style={{ position: 'sticky', top: 0 }}>Количество</th>
                                    <th style={{ position: 'sticky', top: 0 }}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderItems.map(item => (
                                    <tr key={item.id}>
                                        <td>{item.component.name}</td>
                                        <td>{item.component.description}</td>
                                        <td>
                                            {item.component.photo ? (
                                                <img
                                                    src={buildMediaUrl(item.component.photo)}
                                                    alt={item.component.name}
                                                    style={{ width: '50px', cursor: 'pointer' }}
                                                    onClick={() => openImageModal(buildMediaUrl(item.component.photo))}
                                                />
                                            ) : (
                                                'Нет фото'
                                            )}
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value, 10))}
                                                min="1"
                                            />
                                        </td>
                                        <td>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-delete-item">Удалить элемент</Tooltip>}>
                                                <Button variant="danger" onClick={() => handleRemoveItem(item.id)}>
                                                    <i className="fas fa-trash"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <div className="actions mt-3">
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-save-changes">Сохранить изменения</Tooltip>}>
                            <Button variant="primary" onClick={handleSaveChanges}>
                                <i className="fas fa-save"></i>
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-delete-order">Удалить заказ</Tooltip>}>
                            <Button variant="danger" onClick={() => setShowDeleteModal(true)} className="ml-2">
                                <i className="fas fa-trash"></i>
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-export-excel">Экспорт в Excel</Tooltip>}>
                            <Button variant="success" onClick={handleExportExcel} className="ml-2">
                                <i className="fas fa-file-excel"></i>
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-back">Назад</Tooltip>}>
                            <Button variant="secondary" onClick={onClose} className="ml-2">
                                <i className="fas fa-arrow-left"></i>
                            </Button>
                        </OverlayTrigger>
                    </div>

                    {/* Модальное окно для подтверждения удаления заказа */}
                    <Modal
                        show={showDeleteModal}
                        onHide={() => setShowDeleteModal(false)}
                        centered
                        className="warehouse-modal warehouse-confirm-modal"
                        dialogClassName="warehouse-modal-dialog warehouse-modal-dialog--sm"
                        contentClassName="warehouse-modal-content"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Подтверждение удаления</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Вы уверены, что хотите удалить заказ <strong>{orderName}</strong>?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                                Отмена
                            </Button>
                            <Button variant="danger" onClick={handleDeleteOrder}>
                                Удалить
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Модальное окно для увеличенного изображения */}
                    <Modal
                        show={showModal}
                        onHide={() => setShowModal(false)}
                        centered
                        className="warehouse-modal warehouse-media-modal"
                        dialogClassName="warehouse-modal-dialog warehouse-modal-dialog--lg"
                        contentClassName="warehouse-modal-content"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Увеличенное изображение</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <img src={modalImage} alt="component" style={{ width: '100%' }} />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowModal(false)}>
                                Закрыть
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )}
        </div>
    );
}

export default PurchaseOrderDetails;
